import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Box, Button, Typography, Alert, Card, CardContent, CardActionArea, useMediaQuery, useTheme } from '@mui/material';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

type Programmation = {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  description?: string;
  category?: string;
  createdAt: Date;
  updatedAt: Date;
};

// Fonction pour formater le texte de la catégorie
const formatCategory = (category?: string) => {
  if (!category) return '';
  if (category === 'senior') return 'Séniors';
  if (category.startsWith('u')) return category.toUpperCase();
  return category;
};

const Dashboard = () => {
  const { user, isAuthenticated } = useAuthContext();
  const [programmations, setProgrammations] = useState<Programmation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const userId = user?.uid;
  
  console.log('[DASHBOARD] État d\'authentification:', isAuthenticated ? 'Authentifié' : 'Non authentifié');
  console.log('[DASHBOARD] Utilisateur Firebase:', user ? `Présent (${user.uid})` : 'Absent');
  console.log('[DASHBOARD] userId utilisé:', userId);

  useEffect(() => {
    const fetchProgrammations = async () => {
      if (!userId) {
        console.log('[DASHBOARD] Aucun userId disponible, impossible de récupérer les programmations');
        return;
      }

      try {
        console.log('[DASHBOARD] Récupération des programmations pour userId:', userId);
        setIsLoading(true);
        const programmationsRef = collection(db, 'programmations');
        const q = query(
          programmationsRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const programmationsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          startDate: doc.data().startDate?.toDate(),
          endDate: doc.data().endDate?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        })) as Programmation[];

        setProgrammations(programmationsData);
      } catch (err) {
        console.error('Erreur lors de la récupération des programmations:', err);
        setError('Une erreur est survenue lors du chargement de vos programmations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProgrammations();
  }, [userId]);

  const formatDateRange = (startDate: Date, endDate: Date) => {
    return `${format(startDate, 'd MMMM yyyy', { locale: fr })} - ${format(endDate, 'd MMMM yyyy', { locale: fr })}`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-sm h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-8 pt-5">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap={isMobile ? 'wrap' : 'nowrap'} gap={2}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight={700} 
            sx={{ 
              color: 'text.primary',
              width: isMobile ? '100%' : 'auto',
              fontSize: isMobile ? '1.25rem' : '1.75rem',
              textAlign: isMobile ? 'center' : 'left'
            }}
          >
            Planifications
          </Typography>
          <Button
            component={Link}
            to="/programmation/create"
            variant="contained"
            disableElevation
            aria-label="Ajouter une programmation"
            sx={{
              width: isMobile ? '100%' : 'auto',
              minHeight: 40,
              borderRadius: 2,
              px: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: 'primary.main',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'primary.dark',
                boxShadow: 'none',
              },
              '& .MuiButton-startIcon': {
                marginRight: 1,
              }
            }}
            startIcon={<AddCircleOutlineIcon fontSize="small" />}
          >
            Nouvelle planification
          </Button>
        </Box>

        {error && (
          <Box mb={4}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {programmations.length === 0 ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            p={4} 
            mt={4}
            sx={{ 
              backgroundColor: '#ffffff',
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
            }}
          >
            <Typography variant="h6" color="text.primary" textAlign="center" mb={2}>
              Vous n'avez pas encore créé de programmation
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Cliquez sur le bouton + pour créer votre première programmation
            </Typography>
          </Box>
        ) : (
          <div>
            <Box 
              mb={4}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 3,
                '& > *': {
                  minWidth: 0 // Empêche les problèmes de débordement
                }
              }}
            >
              {programmations.map((programmation) => (
                <Card 
                  key={programmation.id}
                  sx={{
                    width: '100%',
                    bgcolor: '#ffffff',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      borderColor: 'rgba(0,0,0,0.14)',
                      textDecoration: 'none',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}>
                      <CardActionArea 
                        component={Link} 
                        to={`/programmation/${programmation.id}/view`} 
                        sx={{ 
                          borderRadius: 2, 
                          p: 0.5,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': {
                            textDecoration: 'none'
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                color: 'text.primary',
                                fontWeight: 700, 
                                textDecoration: 'none',
                                width: '100%',
                                fontSize: '1.1rem',
                                mb: 1,
                                lineHeight: 1.2
                              }}
                            >
                              {programmation.title}
                            </Typography>
                            <Box ml={isMobile ? 0 : 2}>
                              <Typography variant="caption" sx={{
                                bgcolor: 'rgba(0,0,0,0.04)',
                                color: 'text.secondary',
                                px: isMobile ? 1 : 2,
                                py: 0.5,
                                borderRadius: 0.5,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                letterSpacing: 0.2,
                                border: '1px solid rgba(0,0,0,0.10)'
                              }}>
                                ● En cours
                              </Typography>
                            </Box>
                          </Box>
                          <Box mt={1} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" color="text.secondary">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatDateRange(programmation.startDate, programmation.endDate)}
                            </Typography>
                            {programmation.category && (
                              <Typography 
                                variant="caption" 
                                sx={{
                                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                                  color: 'primary.main',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 0.5,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  letterSpacing: 0.2,
                                  textTransform: 'uppercase',
                                  ml: 1
                                }}
                              >
                                {formatCategory(programmation.category)}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
            </Box>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
